import type { RefundRequestDetail } from "@/@types/RefundRequest";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { RefundRequestsService } from "@/services/refund-requests.service";
import useModalStore from "@/store/useModalStore";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  REFUND_REQUEST_DETAIL_QUERY_KEY,
  REFUND_REQUESTS_QUERY_KEY,
  REFUND_REQUESTS_SUMMARY_QUERY_KEY,
  useReloadAfterRefusal,
} from "../hooks/useRefundRequests";

export const APPROVE_REFUND_REQUEST_MODAL_NAME = "approve-refund-request-modal";

interface ApproveRefundRequestModalProps {
  request: RefundRequestDetail | null;
}

export function ApproveRefundRequestModal({ request }: ApproveRefundRequestModalProps) {
  const { t } = useTranslation(["common", "disputes"]);
  const { modals, closeModal } = useModalStore();
  const queryClient = useQueryClient();
  const reloadAfterRefusal = useReloadAfterRefusal();
  const isOpen = modals[APPROVE_REFUND_REQUEST_MODAL_NAME] || false;

  const handleClose = () => closeModal(APPROVE_REFUND_REQUEST_MODAL_NAME);

  const { mutate: approve, isPending } = useMutation({
    mutationFn: async () => {
      if (!request) throw new Error("Refund request not found");
      return RefundRequestsService.approve(request.id);
    },
    onSuccess: (data) => {
      toast({
        description:
          data?.refund_progress?.stage === "team_handling"
            ? t("disputes:approve_modal.success_team_handling")
            : t("disputes:approve_modal.success"),
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: [REFUND_REQUESTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [REFUND_REQUESTS_SUMMARY_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [REFUND_REQUEST_DETAIL_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ["getSellInfo"] });
      handleClose();
    },
    onError: (error: any) => {
      toast({
        description: error?.response?.data?.message || t("common:data_submission_error"),
        variant: "destructive",
      });
      reloadAfterRefusal();
      // 422 é recusa de negócio: o pedido mudou e não há o que tentar de novo neste modal.
      if (error?.response?.status === 422) handleClose();
    },
  });

  const method = request?.payment_method
    ? t(`common:${request.payment_method}`, { defaultValue: request.payment_method })
    : "—";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isPending && handleClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("disputes:approve_modal.title")}</DialogTitle>
          <DialogDescription>
            {t("disputes:approve_modal.description", {
              amount: request?.amount_formatted,
              method,
            })}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
            {t("common:cancel")}
          </Button>
          <Button type="button" onClick={() => approve()} disabled={!request || isPending}>
            {isPending ? t("common:saving") : t("disputes:approve_modal.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
